'use client';

import ease from '@/constants/ease';
import useStore from '@/stores/useStore';
import cx from 'classnames';
import { motion } from 'framer-motion';
import { FC } from 'react';
import Link from './Link';

export interface PlanProps {
  title: string;
  description: string;
  cta: {
    title: string;
    href: string;
    action: 'trial' | 'demo'
  };
  featured?: boolean;
  tag?: string;

  monthlyPrice?: string;
  monthlyPriceSubtitle?: string;
  monthlyPriceDescription?: string;
  yearlyPrice?: string;
  yearlyPriceSubtitle?: string;
  yearlyPriceDescription?: string;
  disableMonthlyPricing?: boolean;
}

export interface PlanTileProps extends PlanProps {
  isYearlyPricingVisible: boolean;
  onToggleYearlyPricing: () => void;
}

export const PlanTile: FC<PlanTileProps> = ({
  isYearlyPricingVisible,
  onToggleYearlyPricing,
  title,
  description,
  cta,
  tag,
  featured,
  monthlyPrice,
  monthlyPriceSubtitle,
  monthlyPriceDescription,
  yearlyPrice,
  yearlyPriceSubtitle,
  yearlyPriceDescription,
  disableMonthlyPricing,
}) => {
  let monthlyPricing;
  const { setDemoModalOpen, setTrialModalOpen } = useStore();

  const modalActionHandler = (action) => {
    switch (action) {
      case 'trial':
        setTrialModalOpen(true);
        break;
      case 'demo':
        setDemoModalOpen(true);
        break;
      default:
        break;
    }
  };

  if (disableMonthlyPricing) {
    monthlyPricing = (
      <div className="h-[90px] text-center flex flex-col justify-center items-center text-gray-600">
        <p>
          Only available on{' '}
          <button onClick={onToggleYearlyPricing} className="link-underline">
            annual
          </button>
        </p>
      </div>
    );
  } else {
    monthlyPricing = (
      <div className="flex flex-col gap-half">
        <p className="flex items-baseline gap-half">
          <span className="font-500 text-66/66 xl:text-44/44 1400:text-66/66 tracking-tight">{monthlyPrice}</span>
          <span className="text-gray-700 text-17/24">{monthlyPriceSubtitle}</span>
        </p>
        <p className="text-gray-700 text-14/16">{monthlyPriceDescription}</p>
      </div>
    );
  }

  const yearlyPricing = (
    <div className="flex flex-col gap-half">
      <p className="flex items-baseline gap-half">
        <span className={`font-500  ${title === "Premier" ? "text-42/42 xl:text-34/34 1400:text-42/42" : "text-66/66 xl:text-44/44 1400:text-66/66"} tracking-tight`}>{yearlyPrice}</span>
        <span className="text-gray-700 text-17/24">{yearlyPriceSubtitle}</span>
      </p>
      <p className="text-gray-700 text-14/16">{yearlyPriceDescription}</p>
    </div >
  );

  const header = (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-25/30 font-500">{title}</h3>
        {tag && (
          <span className="bg-citron-500 text-citron-900 block rounded-4 px-half text-10/20 h-[20px] uppercase font-mono font-800 whitespace-nowrap">
            {tag}
          </span>
        )}
      </div>
      <div className="min-h-[90px]">{isYearlyPricingVisible ? yearlyPricing : monthlyPricing}</div>
      <hr className="border-violet-200" />
      <p className="text-14/20">{description}</p>
    </div>
  );

  const footer = (
    <div className="flex flex-col gap-3">
      {cta.href.length > 0 ? (
        <Link className="block w-full button button-size-medium button-primary-violet" href={cta.href}>
          {cta.title}
        </Link>
      ) : (
        <button
          onClick={() => {
            modalActionHandler(cta.action);
          }}
          className="block w-full button button-size-medium button-primary-violet"
        >
          {cta.title}
        </button>
      )}
    </div>
  );

  return (
    <div
      className={cx('bg-white rounded-30 outline outline-[8px] h-full p-4 flex flex-col gap-3 justify-between', {
        'outline-violet-200': !featured,
        'outline-violet-500 shadow-plan-tile': featured,
      })}
    >
      {header}
      {footer}
    </div>
  );
};
export interface PricingHeroPlanComparisonProps {
  title: string;
  annualBillingTitle: string;
  annualBillingSubtitle: string;

  plans: PlanProps[];

  logosTitle: string;
  companyLogos: React.ReactNode[];

  isYearlyPricingVisible: boolean;
  setYearlyPricingVisible: (boolean) => void;
}

const PricingHeroPlanComparison: FC<PricingHeroPlanComparisonProps> = ({
  title,
  annualBillingTitle,
  annualBillingSubtitle,
  plans = [],
  logosTitle,
  companyLogos = [],
  isYearlyPricingVisible,
  setYearlyPricingVisible,
}) => {
  const onToggleYearlyPricing = () => {
    setYearlyPricingVisible(!isYearlyPricingVisible);
  };

  const pricingSwitch = (
    <div className="flex items-center gap-2 relative">
      <button
        onClick={onToggleYearlyPricing}
        className={cx('h-4 w-9 rounded-10 link-cover transition-colors duration-300 ease-quad', {
          'bg-violet-700': isYearlyPricingVisible,
          'bg-violet-300': !isYearlyPricingVisible,
        })}
      >
        <motion.div
          className="h-[20px] w-[20px] bg-white rounded-4"
          initial={false}
          animate={{
            x: isYearlyPricingVisible ? 46 : 6,
          }}
          transition={{
            ease: ease.appear,
            duration: 400 / 1000,
          }}
        />
      </button>
      <div className="flex items-center gap-1">
        <p className="text-17/24">{annualBillingTitle}</p>
        <span className="bg-violet-300 text-violet-900 block rounded-4 px-half text-10/20 h-[20px] uppercase font-mono font-800">
          {annualBillingSubtitle}
        </span>
      </div>
    </div>
  );

  const planGrid = (
    <div>
      <div className="grid gap-6 md:gap-container-mobile grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 bn-grid">
        {plans.map((plan, index) => (
          <PlanTile
            key={`plan-tile-${plan.title?.replace(/\s/g, '-') ?? index}`}
            isYearlyPricingVisible={isYearlyPricingVisible}
            onToggleYearlyPricing={onToggleYearlyPricing}
            {...plan}
          />
        ))}
      </div>
    </div>
  );

  const header = (
    <div className="flex flex-col items-center gap-6">
      <h1 className="text-36/42 md:text-56/62 font-500 tracking-tight md:text-center text-pretty">{title}</h1>
      {pricingSwitch}
      {planGrid}
    </div>
  );

  const logoFooter = (
    <div className="flex flex-col gap-6 md:gap-8">
      <h2 className="text-25/30 font-500 text-center">{logosTitle}</h2>
      <div className="grid grid-cols-2 gap-x-gutter-mobile gap-y-4 md:flex md:gap-10 md:flex-wrap md:items-center md:justify-center">
        {companyLogos}
      </div>
    </div>
  );

  return (
    <div className="bg-violet-100 text-violet-900">
      <div className="-mt-header-height-mobile md:-mt-header-height-desktop pt-header-height-mobile md:pt-header-height-desktop">
        <div className="pt-5 md:pt-12 px-container-mobile md:px-container-desktop pb-8">
          <div className="mx-auto max-w-grid">
            <div className="flex flex-col gap-8">
              {header}
              {logoFooter}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingHeroPlanComparison;
